import type { SvgIconComponent } from '@mui/icons-material'
import BarChartIcon from '@mui/icons-material/BarChart'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import PeopleIcon from '@mui/icons-material/People'
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler'
import BrushIcon from '@mui/icons-material/Brush'
import RouteIcon from '@mui/icons-material/Route'
import SettingsIcon from '@mui/icons-material/Settings'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'

/** A feature flag that can gate a nav item. Mirrors FeatureFlagContext keys. */
export type NavFlag = 'vehicle' | 'drawOnTrack'

export interface NavItem {
  label: string
  to: string
  icon: SvgIconComponent
  /** Exact-match active state (use for the index route). */
  end?: boolean
  /** When set, the item is disabled unless the flag is on. */
  flag?: NavFlag
  /** Shown only to an admin account. */
  adminOnly?: boolean
}

export const primaryNav: NavItem[] = [
  { label: 'Analysis', to: '/v2/analysis', icon: BarChartIcon },
  {
    label: 'Telemetry',
    to: '/v2/analysis/telemetry',
    icon: ShowChartIcon,
  },
  { label: 'Compare', to: '/v2/compare', icon: PeopleIcon },
  {
    label: 'Vehicles',
    to: '/v2/vehicles',
    icon: TwoWheelerIcon,
    flag: 'vehicle',
  },
]

export const secondaryNav: NavItem[] = [
  {
    label: 'Draw on track',
    to: '/v2/draw-on-track',
    icon: BrushIcon,
    flag: 'drawOnTrack',
  },
  { label: 'Suggested tracks', to: '/v2/suggested-tracks', icon: RouteIcon },
  { label: 'Settings', to: '/v2/settings', icon: SettingsIcon },
  {
    label: 'Admin',
    to: '/v2/admin',
    icon: AdminPanelSettingsIcon,
    adminOnly: true,
  },
]

/**
 * Human label for the current /v2 path, used by the top-bar breadcrumb.
 * Session detail routes collapse to a single "Analyse session" crumb;
 * anything unrecognized falls back to "Analysis".
 */
export function v2Breadcrumb(pathname: string): string {
  if (pathname.startsWith('/v2/analysis/telemetry')) {
    return 'Telemetry'
  }
  if (pathname.startsWith('/v2/analysis/sessions')) {
    return 'Analyse session'
  }
  const item = [...primaryNav, ...secondaryNav].find(
    (i) => pathname === i.to || pathname.startsWith(i.to + '/')
  )
  return item?.label ?? 'Analysis'
}
